import * as React from 'react';
import moment from 'moment';
import {
	nullSafeGet,
	nullSafeGetOrElse,
	getWorkOrderTimeZoneId,
} from '../../utils/DataAccessUtils';
import { DATE_ONLY_FORMAT, dateFormatter } from '../../utils/DataFormatterUtils';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { ArrowRightOutlined, SearchOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import WorkOrderDisplayStatusDisplay from '../work_order_display_status_display/WorkOrderDisplayStatusDisplay';
import WorkOrderStatusDisplay from '../work_order_status_display/WorkOrderStatusDisplay';
import OWLink from '../OWLink';
import DateTimeHover from '../date_time_component/DateTime';
import { connect } from 'react-redux';
import { getWorkOrderPath } from '../../utils/WorkOrderUtils';

require('./ServiceCallPopoverDisplay.less');

const ServiceCallPopoverDisplayV2Base: React.FC<any> = ({ workOrder, currentUser }) => {
	if (!workOrder) {
		return null;
	}

	const workOrderDetailPath = `/supplier/workOrders/detail/${nullSafeGet(
		'id',
		workOrder
	)}/${getWorkOrderPath(ROLE_TYPES.SUPPLIER, currentUser, workOrder)}details`;

	const lastServiceCall = nullSafeGetOrElse('lastServiceCall', workOrder, {});
	const checkInTime = nullSafeGet('checkInTime', lastServiceCall);
	const checkOutTime = nullSafeGet('checkOutTime', lastServiceCall);
	const serviceScheduledAt = nullSafeGet('serviceScheduledAt', lastServiceCall);

	const timeZone = getWorkOrderTimeZoneId(workOrder);

	let label = 'Scheduled';
	let timestamp = serviceScheduledAt;

	if (checkOutTime && checkInTime) {
		label = 'Service time';
		timestamp = checkInTime;
	} else if (checkInTime) {
		label = 'Checked in';
		timestamp = checkInTime;
	}

	const duration =
		checkOutTime && checkInTime ? moment(checkInTime).from(moment(checkOutTime), true) : null;

	return (
		<div className="flex min-w-[400px] max-w-[50vw] flex-col gap-3 px-2 py-1">
			<div className="flex flex-col gap-3">
				<div className="flex flex-row items-center gap-2">
					<WorkOrderDisplayStatusDisplay status={nullSafeGet('displayStatus', workOrder)} />
					<WorkOrderStatusDisplay
						displayStatus={nullSafeGet('displayStatus', workOrder)}
						status={nullSafeGet('status', workOrder)}
						showDisplayStatus={false}
					/>
				</div>
				<div className="flex flex-col gap-2">
					<div className="h5medium text-character-primary">{nullSafeGet('title', workOrder)}</div>
					{timestamp && (
						<div className="flex flex-row items-center gap-1">
							<span className="bodyregular text-character-secondary">
								{label}:{' '}
								<DateTimeHover
									timestamp={timestamp}
									dateFormat={'h:mm a z on MMM D'}
									showDate
									timeZone={timeZone}
								/>
								{checkOutTime && checkInTime && (
									<>
										{' - '}
										<DateTimeHover
											timestamp={checkOutTime}
											dateFormat={'h:mm a z on MMM D'}
											showDate
											parseZone={false}
											timeZone={timeZone}
										/>
										{duration && ` (${duration})`}
									</>
								)}
							</span>
						</div>
					)}
				</div>
			</div>
			<div className="bodyregular flex flex-row items-center gap-4 text-character-secondary">
				<SearchOutlined />{' '}
				<OWLink
					preserveBackLinkStates={true}
					backText="Back to service schedule"
					to={workOrderDetailPath}
					target="_blank"
				>
					WO #{nullSafeGet('id', workOrder)}
				</OWLink>
			</div>
			<div className="bodyregular flex flex-row items-center gap-4 text-character-secondary">
				<div>
					<i className={`icons8-font icons8-location-logo-2`} />
				</div>
				<div>{nullSafeGet('location.name', workOrder)}</div>
			</div>
			<div className="bodyregular flex flex-row items-center gap-4 text-character-secondary">
				<div>
					<i className={`icons8-font icons8-company-2`} />
				</div>
				<div>{nullSafeGet('supplierFacility.displayName', workOrder)}</div>
			</div>
			{nullSafeGet('lastServiceCall.leadTechnicianEmailContact.nameGiven', workOrder) ? (
				<div className="bodyregular flex flex-row items-center gap-4 text-character-secondary">
					<div>
						<i className={`icons8-font icons8-worker`} />
					</div>
					<div>
						{nullSafeGetOrElse('lastServiceCall.additionalTechnicians', workOrder, []).length >
						0 ? (
							<div className="bodyregular flex flex-row items-center gap-4 text-character-secondary">
								{nullSafeGetOrElse('lastServiceCall.additionalTechnicians', workOrder, []).length +
									1}{' '}
								technicians
							</div>
						) : null}
						{nullSafeGet('lastServiceCall.leadTechnicianEmailContact', workOrder) ? (
							<div className="bodyregular flex flex-row items-center gap-4 text-character-secondary">
								<ContactAvatar
									size="small"
									hidePopover={true}
									contact={nullSafeGet('lastServiceCall.leadTechnicianEmailContact', workOrder)}
									userType={ROLE_TYPES.BUYER}
								/>
								<div>
									{nullSafeGet('lastServiceCall.leadTechnicianEmailContact.nameGiven', workOrder)}{' '}
									{nullSafeGet('lastServiceCall.leadTechnicianEmailContact.nameFamily', workOrder)}
								</div>
							</div>
						) : null}
						{nullSafeGet('lastServiceCall.additionalTechnicians', workOrder).map((contact) => (
							<div className="bodyregular flex flex-row items-center gap-4 text-character-secondary">
								<ContactAvatar
									size="small"
									hidePopover={true}
									contact={contact}
									userType={ROLE_TYPES.BUYER}
								/>
								<div>
									{contact.nameGiven} {contact.nameFamily}
								</div>
							</div>
						))}
					</div>
				</div>
			) : null}
			<div className="bodyregular flex flex-row items-center gap-4 text-character-secondary">
				<div>
					<i className={`icons8-font icons8-engineer-symbol`} />
				</div>
				<div>{nullSafeGet('problemType.hierarchyName', workOrder)}</div>
			</div>
			<div className="bodyregular flex flex-row items-center gap-4 text-character-secondary">
				<div>
					<i className={`icons8-font icons8-service-bell`} />
				</div>
				<div>{nullSafeGet('woPriority.name', workOrder)}</div>
			</div>
			<div className="bodyregular flex flex-row items-center gap-4 text-character-secondary">
				<div>
					<i className="icons8-font icons8-green-check-mark"></i>
				</div>
				<div>
					Est. Completion{' '}
					{dateFormatter(
						nullSafeGetOrElse(
							'estimatedCompletionDate',
							workOrder,
							nullSafeGet('dueDate', workOrder)
						),
						DATE_ONLY_FORMAT
					)}
				</div>
			</div>
			<OWLink
				preserveBackLinkStates={true}
				backText="Back to service schedule"
				to={workOrderDetailPath}
				className="w-full"
				target="_blank"
			>
				<Button className="w-full" icon={<ArrowRightOutlined />}>
					View Work Order
				</Button>
			</OWLink>
		</div>
	);
};

export const ServiceCallPopoverDisplayV2 = connect(
	(state: any) => ({ currentUser: state.session.currentUser }),
	() => ({})
)(ServiceCallPopoverDisplayV2Base);
